import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { type SolarDay, solarApi } from "@/api/solar";
import { PeriodNav } from "@/components/PeriodNav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PLANT_COLORS } from "@/lib/segmentColors";

import {
  type Period,
  calendarRangeFor,
  fmt as fmtCanonical,
  num as numCanonical,
  todayIso,
} from "@/lib/format";

/** Для сум: `null` рахуємо як 0, бо це доданок. */
const num = (v: number | string | null | undefined): number => numCanonical(v) ?? 0;
const fmt = (n: number | null, digits = 0): string => fmtCanonical(n, digits);

/** Перелік днів [from, to] включно, як YYYY-MM-DD. */
function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(from + "T00:00:00Z");
  const end = new Date(to + "T00:00:00Z");
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/** Сторінка «СЕС» (FR-SOLAR-08). Показує відпуск у мережу, виручку за
 *  фіксованим тарифом, споживання станції з мережі та прибуток. */
export function SolarPage() {
  const { t } = useTranslation();
  const [end, setEnd] = useState(todayIso());
  const [period, setPeriod] = useState<Period>("month");
  const [plantId, setPlantId] = useState<string | null>(null); // null = всі станції

  const { from, to } = useMemo(() => calendarRangeFor(end, period), [end, period]);

  const plants = useQuery({
    queryKey: ["solar-plants"],
    queryFn: () => solarApi.listPlants(),
  });

  const daily = useQuery({
    queryKey: ["solar-daily", from, to, plantId],
    queryFn: () => solarApi.daily(from, to, { plantId: plantId ?? undefined }),
  });

  const plantName = (id: string | null): string => {
    if (!id) return t("solar.all_plants");
    return plants.data?.find((p) => p.id === id)?.name ?? id.slice(0, 8);
  };

  const totals = useMemo(() => {
    const rows = (daily.data ?? []).filter((r) => r.hours > 0);
    if (rows.length === 0) {
      // Жодної години за період — це «немає даних», а не «нуль».
      return { exportKwh: null, importKwh: null, revenue: null, profit: null, gaps: 0 };
    }
    let exportKwh = 0;
    let importKwh = 0;
    let revenue = 0;
    let profit = 0;
    let gaps = 0;
    for (const r of rows) {
      exportKwh += num(r.export_kwh);
      importKwh += num(r.import_kwh);
      revenue += num(r.revenue_uah);
      profit += num(r.profit_uah);
      gaps += Math.max(0, r.hours_expected - r.hours);
    }
    return { exportKwh, importKwh, revenue, profit, gaps };
  }, [daily.data]);

  /** Дані графіка: по днях, серія на кожну станцію (або одна, якщо обрана). */
  const chart = useMemo(() => {
    const rows = daily.data ?? [];
    const byDay = new Map<string, Record<string, number | string | undefined>>();
    for (const r of rows) {
      if (r.hours === 0) continue; // діра в даних — стовпчика не малюємо
      const key = r.day.slice(5);
      const row = byDay.get(key) ?? { day: key };
      const series = r.plant_id ? plantName(r.plant_id) : t("solar.all_plants");
      row[series] = num(row[series] as number) + num(r.export_kwh);
      byDay.set(key, row);
    }
    return Array.from(byDay.values());
  }, [daily.data, plants.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const seriesNames = useMemo(() => {
    const s = new Set<string>();
    for (const r of daily.data ?? []) {
      s.add(r.plant_id ? plantName(r.plant_id) : t("solar.all_plants"));
    }
    return Array.from(s);
  }, [daily.data, plants.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ті самі кольори станцій, що й на «Фінансах» (зелений там зайнятий КГУ).
  const COLORS = PLANT_COLORS;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("solar.title")}</CardTitle>
          <CardDescription>{t("solar.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <PeriodNav
            period={period}
            refDate={end}
            onPeriodChange={setPeriod}
            onRefDateChange={setEnd}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={plantId === null ? "default" : "outline"}
              size="sm"
              onClick={() => setPlantId(null)}
            >
              {t("solar.all_plants")}
            </Button>
            {(plants.data ?? []).map((p) => (
              <Button
                key={p.id}
                variant={plantId === p.id ? "default" : "outline"}
                size="sm"
                onClick={() => setPlantId(p.id)}
              >
                {p.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {(plants.data ?? []).length === 0 && !plants.isLoading && (
        <Card>
          <CardContent className="p-4 text-sm text-slate-500">
            {t("solar.no_plants")}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label={t("solar.export_total")}
          value={
            totals.exportKwh === null
              ? "—"
              : `${fmt(totals.exportKwh / 1000, 1)} ${t("statistics.unit_mwh")}`
          }
        />
        <Stat
          label={t("solar.revenue_total")}
          value={totals.revenue === null ? "—" : `${fmt(totals.revenue, 0)} ${t("common.uah")}`}
        />
        <Stat
          label={t("solar.import_total")}
          value={
            totals.importKwh === null ? "—" : `${fmt(totals.importKwh, 1)} ${t("common.kwh")}`
          }
        />
        <Stat
          label={t("solar.profit_total")}
          value={totals.profit === null ? "—" : `${fmt(totals.profit, 0)} ${t("common.uah")}`}
        />
      </div>

      {totals.gaps > 0 && (
        <Card>
          <CardContent className="p-3 text-sm text-amber-700 bg-amber-50 rounded-md">
            {t("solar.gaps_warning", { count: totals.gaps })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("solar.chart_export")}</CardTitle>
        </CardHeader>
        <CardContent>
          {chart.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Legend />
                  {seriesNames.map((name, i) => (
                    <Bar
                      key={name}
                      dataKey={name}
                      stackId="export"
                      fill={COLORS[i % COLORS.length]}
                      name={name}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("solar.table_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <DailyTable
            rows={daily.data ?? []}
            plantName={plantName}
            days={eachDay(from, to)}
            plantIds={
              plantId ? [plantId] : (plants.data ?? []).map((p) => p.id)
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}

function DailyTable({
  rows,
  plantName,
  days,
  plantIds,
}: {
  rows: SolarDay[];
  plantName: (id: string | null) => string;
  days: string[];
  plantIds: string[];
}) {
  const { t } = useTranslation();

  /** Повна сітка «доба × станція»: доби без жодної години теж мають бути
   *  видимі — інакше діра в даних виглядає як нормальний період. */
  const grid = useMemo(() => {
    const byKey = new Map<string, SolarDay>();
    for (const r of rows) byKey.set(`${r.day}|${r.plant_id ?? "all"}`, r);
    const ids: (string | null)[] = plantIds.length > 0 ? plantIds : [null];
    const out: { day: string; plantId: string | null; row: SolarDay | null }[] = [];
    for (const day of days) {
      for (const id of ids) {
        out.push({ day, plantId: id, row: byKey.get(`${day}|${id ?? "all"}`) ?? null });
      }
    }
    return out;
  }, [rows, days, plantIds]);

  if (grid.length === 0) {
    return <div className="text-sm text-slate-500">{t("home1.no_data")}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="p-2">{t("solar.col_day")}</th>
            <th className="p-2">{t("solar.col_plant")}</th>
            <th className="p-2 text-right">{t("solar.col_export")}</th>
            <th className="p-2 text-right">{t("solar.col_import")}</th>
            <th className="p-2 text-right">{t("solar.col_revenue")}</th>
            <th className="p-2 text-right">{t("solar.col_profit")}</th>
            <th className="p-2 text-right">{t("solar.col_hours")}</th>
          </tr>
        </thead>
        <tbody>
          {grid.map(({ day, plantId, row }) => {
            // hours === 0 (або рядка немає) — даних немає: скрізь «—», не «0».
            const hasData = row !== null && row.hours > 0;
            const partial = hasData && row.hours < row.hours_expected;
            const cell = (v: number | string | null | undefined, digits: number) =>
              hasData ? fmt(numCanonical(v), digits) : "—";
            return (
              <tr
                key={`${day}-${plantId ?? "all"}`}
                className={`border-t border-slate-100 ${hasData ? "" : "text-slate-400"}`}
              >
                <td className="p-2">{day}</td>
                <td className="p-2">{plantName(plantId)}</td>
                <td className="p-2 text-right font-mono">{cell(row?.export_kwh, 1)}</td>
                <td className="p-2 text-right font-mono">{cell(row?.import_kwh, 1)}</td>
                <td className="p-2 text-right font-mono">{cell(row?.revenue_uah, 0)}</td>
                <td className="p-2 text-right font-mono">{cell(row?.profit_uah, 0)}</td>
                <td
                  className={`p-2 text-right font-mono ${partial ? "text-amber-600" : "text-slate-400"}`}
                  title={
                    hasData
                      ? t("solar.hours_hint", {
                          hours: row.hours,
                          expected: row.hours_expected,
                        })
                      : t("solar.no_data_hint")
                  }
                >
                  {hasData ? `${row.hours}/${row.hours_expected}` : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-mono font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
